import { useId, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Btn,
  DonateAmountPicker,
  ProgressBar,
  SmartLink,
  SplitPanels,
  VideoLightbox,
  useLocale,
  useSeo,
  useText,
} from '@kkf/design'
import { useAppeals } from '../api/useAppeals'
import { GiveForm } from '../components/GiveForm'
import { formatMoney, type DonationCause, type DonationStatus } from '../data/donations'
import { DONATE_AMOUNTS } from '../data/donation-details'
import { PARTNERS_AND_GIVE } from '../data/home'
import { DONATIONS_ROUTE, SITE } from '../data/seo'
import { useT } from '../i18n'

/**
 * The donations page — a directory of appeals, not a catalogue of cards.
 *
 * ## What it was, and why it changed (2026-09-19)
 *
 * A four-across grid of `DonationCard`s with `Pagination` under it. Three things were
 * wrong with that and only the first is cosmetic:
 *
 *  - The cards sat in a `row` with no gutters, so four appeals read as one slab.
 *  - **The pagination was furniture.** There were four causes and the control paginated
 *    nothing; it was the template's, and it implied a fifth page that did not exist.
 *    (Two causes as of 2026-09-21, which makes the point harder still.)
 *  - Choosing between four appeals means comparing them, and a grid of equal cards each
 *    truncated to two lines is the layout that makes comparison hardest. A reader had to
 *    leave the page to read one, and leave the page again to read the next.
 *
 * So: the appeals on the left, the one you are reading on the right, and the give action in
 * the same panel as the words that argued for it.
 *
 * ## The state is the query string
 *
 * `?cause=` and `?show=`, the pattern `app_resort`'s `/book` set and `app_partners`'s board
 * followed. A chosen appeal can be reloaded, sent to somebody and reached with the back
 * button. It also means the rail is **links**, not buttons — which is what it should be
 * either way, since each one goes somewhere.
 *
 * A selected view marks itself `noindex`: those are the same words `/donations/<slug>`
 * already serves, and two URLs for one appeal is the duplicate-content problem. The bare
 * route stays indexed and stays in the sitemap. The page writes that in a passive effect
 * and the shell writes the route's head in a layout one, which is the whole mechanism —
 * see `useRouteSeo`.
 *
 * ## The appeals come from the API (2026-09-25)
 *
 * `GET /v1/donations/appeals`, through `useAppeals` — they are written in Steward now, not in
 * `data/donations.ts`. So the page has two states it never had: **loading**, and **could not
 * load**. Both keep the heading and say which they are in one line; neither is a blank page,
 * and the second offers to try again, because a network's failure is not the Foundation
 * having no appeals.
 *
 * ## The giving form is always in view
 *
 * Personal info, the anonymous choice and a note sit **above** the amounts and are never
 * behind "Read the full appeal" — see `GiveForm`, shared with `/donations/:slug`. The
 * disclosure opens the rest of the prose and nothing else.
 *
 * ## No invented figures
 *
 * The gift count, the raised figure and the bar are the API's, summed from **completed**
 * payments only. There is no payment provider yet, so all three are zero; the count reads
 * "Be the first to give" and the raised figure "figure to confirm", as they did when they
 * were typed into a data module. A gift count is the most persuasive number a donation page
 * can carry — which is precisely why it must only ever be counted.
 */
export function DonationsPage() {
  const t = useT()
  const s = useText()
  const { intlLocale } = useLocale()
  const [params, setParams] = useSearchParams()
  const appeals = useAppeals()
  const causes = appeals.status === 'loaded' ? appeals.causes : []
  const activeCauses = causes.filter((c) => c.status === 'active')
  const pastCauses = causes.filter((c) => c.status === 'past')

  const show: DonationStatus = params.get('show') === 'past' ? 'past' : 'active'
  const query = (params.get('q') ?? '').trim().toLowerCase()

  const inTab = show === 'past' ? pastCauses : activeCauses
  /*
   * The search is over what is on the row — the appeal's name and its tag — and nothing
   * else. Searching the body as well would return rows whose visible text does not contain
   * what was typed, which reads as a broken filter rather than a clever one.
   */
  const listed = query
    ? inTab.filter((cause) =>
        [cause.title.en, cause.title.mn, cause.tag.en, cause.tag.mn]
          .join(' ')
          .toLowerCase()
          .includes(query),
      )
    : inTab

  const asked = params.get('cause')
  // An unknown or out-of-tab slug falls back to the first appeal in view rather than to an
  // empty panel: a mistyped query string should not look like a broken page.
  const selected: DonationCause | undefined =
    listed.find((cause) => cause.slug === asked) ?? listed[0]
  /** The opening paragraph stays in the panel; the rest opens with the full appeal. */
  const [lead, ...rest] = selected?.body ?? []

  useSeo(SITE, { ...DONATIONS_ROUTE, noindex: Boolean(asked) || show === 'past' || Boolean(query) })

  /*
   * Whether the full appeal is open, and it is `useState` rather than the query string —
   * unlike every other piece of state on this page.
   *
   * The others answer "what am I looking at", which is worth sending to somebody and worth
   * surviving a reload. This one answers "have I opened the drawer", which is not; putting
   * it in the URL would put a `&full=1` on every shared link for no gain.
   *
   * **It resets when the chosen appeal changes, and so does `shot`** — explicitly, below.
   * This comment used to say the keyed panel did it, and it did not: both live here, above
   * the `key`, so a reader who opened one appeal on its third photograph found the next one
   * already expanded, on its third. Found in review on 2026-09-25, when appeals added in
   * Steward made a third appeal reachable.
   */
  const [full, setFull] = useState(false)
  const fullId = useId()
  /** Which photograph the panel is showing. */
  const [shot, setShot] = useState(0)
  // React's "adjust state when a prop changes" pattern: during render, not in an effect, so
  // the new appeal never paints once with the old one's drawer open.
  const [shownSlug, setShownSlug] = useState(selected?.slug)
  if (shownSlug !== selected?.slug) {
    setShownSlug(selected?.slug)
    setFull(false)
    setShot(0)
  }

  const tab = (next: DonationStatus) => {
    const q = new URLSearchParams(params)
    if (next === 'active') q.delete('show')
    else q.set('show', next)
    // The chosen cause belongs to the tab it was chosen in, so it does not survive a
    // switch — carrying it over would select a slug the new list does not contain.
    q.delete('cause')
    return `?${q}`
  }

  const href = (slug: string) => {
    const q = new URLSearchParams(params)
    q.set('cause', slug)
    return `?${q}`
  }

  const search = (next: string) => {
    const q = new URLSearchParams(params)
    if (next) q.set('q', next)
    else q.delete('q')
    // The chosen appeal may not survive the filter, and `selected` falls back to the first
    // row in view rather than emptying the panel.
    q.delete('cause')
    setParams(q, { replace: true })
  }

  return (
    <>
      <section className="ul-dondir ul-section-spacing ul-plain-page-top">
        <div className="ul-container">
          <header className="ul-dondir-head wow animate__fadeInUp">
            <span className="ul-section-sub-title ul-section-sub-title--2">{t('donate.eyebrow')}</span>
            {/* The page's one `<h1>`. It was `Breadcrumb`'s title until the banner went. */}
            <h1 className="ul-section-title">{t('donate.heading')}</h1>
            <p className="ul-section-descr">{t('donate.intro')}</p>
          </header>

          <div className="ul-dondir-body">
            <aside className="ul-dondir-rail">
              {/*
                The search. `type="search"`, so a phone offers the right keyboard and the
                browser its own clear button, and `replace: true` on the history entry —
                typing eight characters should not be eight presses of the back button.
              */}
              <div className="ul-dondir-search">
                <i className="flaticon-search" aria-hidden="true" />
                <input
                  type="search"
                  value={params.get('q') ?? ''}
                  onChange={(event) => search(event.target.value)}
                  placeholder={t('donate.search')}
                  aria-label={t('donate.search')}
                />
              </div>

              <nav className="ul-dondir-tabs" aria-label={t('donate.tabs.label')}>
                <SmartLink
                  to={tab('active')}
                  className={show === 'active' ? 'ul-dondir-tab is-current' : 'ul-dondir-tab'}
                  aria-current={show === 'active' ? 'page' : undefined}
                >
                  {t('donate.tab.active')}
                  {appeals.status === 'loaded' && (
                    <span className="ul-dondir-tab-count">{activeCauses.length}</span>
                  )}
                </SmartLink>
                <SmartLink
                  to={tab('past')}
                  className={show === 'past' ? 'ul-dondir-tab is-current' : 'ul-dondir-tab'}
                  aria-current={show === 'past' ? 'page' : undefined}
                >
                  {t('donate.tab.past')}
                  {appeals.status === 'loaded' && (
                    <span className="ul-dondir-tab-count">{pastCauses.length}</span>
                  )}
                </SmartLink>
              </nav>

              {appeals.status === 'loading' ? (
                <p className="ul-dondir-state" role="status">
                  {t('donate.loading')}
                </p>
              ) : appeals.status === 'failed' ? (
                <p className="ul-dondir-state" role="alert">
                  {t('donate.failed')}
                  <button type="button" onClick={appeals.retry}>
                    {t('donate.retry')}
                  </button>
                </p>
              ) : listed.length === 0 ? (
                /*
                 * A real empty state, and it says which of the two empties this is. No
                 * appeal has closed yet — that is a different fact from "we have no
                 * appeals", and a reader acts differently on each.
                 */
                <p className="ul-dondir-empty">
                  {query ? t('donate.search.empty', { query }) : t('donate.past.empty')}
                </p>
              ) : (
                <ul className="ul-dondir-list">
                  {listed.map((cause) => {
                    const current = cause.slug === selected?.slug
                    return (
                      <li key={cause.slug}>
                        <SmartLink
                          to={href(cause.slug)}
                          className={current ? 'ul-dondir-item is-current' : 'ul-dondir-item'}
                          aria-current={current ? 'true' : undefined}
                        >
                          <img
                            className="ul-dondir-item-thumb"
                            src={cause.image}
                            alt=""
                            loading="lazy"
                          />
                          <span className="ul-dondir-item-body">
                            <span className="ul-dondir-item-tag">{s(cause.tag)}</span>
                            <span className="ul-dondir-item-title">{s(cause.title)}</span>
                            <span className="ul-dondir-item-bar">
                              <span
                                className="ul-dondir-item-bar-fill"
                                style={{ width: `${cause.progress}%` }}
                              />
                            </span>
                            <span className="ul-dondir-item-meta">
                              <span>{t('donate.progress', { percent: cause.progress })}</span>
                              <span className="ul-dondir-item-people">
                                <i className="flaticon-team" aria-hidden="true" />
                                {cause.donations}
                              </span>
                            </span>
                          </span>
                        </SmartLink>
                      </li>
                    )
                  })}
                </ul>
              )}
            </aside>

            {selected && (
              <article className="ul-dondir-detail" key={selected.slug}>
                {/*
                  The media and the headline share a row, and the photograph is a fixed
                  240px rather than a full-bleed 16:7 band.

                  It was the band for a few hours on 2026-09-19 and it cost the panel its
                  whole point: at 830px wide the picture alone was 360px tall, so the appeal
                  you had just clicked began below the fold and the meter and the amounts
                  were two scrolls away. A directory is for seeing one thing whole.
                */}
                <div className="ul-dondir-detail-head">
                  <div className="ul-dondir-detail-media">
                    <div className="ul-dondir-detail-shot">
                      <img
                        src={selected.images[shot] ?? selected.images[0] ?? selected.image}
                        alt={selected.imageAlt ? s(selected.imageAlt) : ''}
                      />
                      {/*
                        A play button over the lead image where the appeal has a film. The
                        lightbox is the design system's — the same one `/services/:slug`
                        uses — so there is one way a video opens on this site.
                      */}
                      {selected.video && (
                        <VideoLightbox source={selected.video} className="ul-dondir-detail-play">
                          <i className="flaticon-play" aria-hidden="true" />
                          <span className="visually-hidden">{t('video.play')}</span>
                        </VideoLightbox>
                      )}
                    </div>

                    {/* The strip, only where there is more than one photograph. */}
                    {selected.images.length > 1 && (
                      <ul className="ul-dondir-detail-thumbs">
                        {selected.images.map((src, i) => (
                          <li key={src}>
                            <button
                              type="button"
                              className={i === shot ? 'is-current' : undefined}
                              onClick={() => setShot(i)}
                              aria-label={t('donate.photo', { n: i + 1 })}
                              aria-current={i === shot ? 'true' : undefined}
                            >
                              <img src={src} alt="" loading="lazy" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="ul-dondir-detail-heading">
                    <span className="ul-dondir-detail-tag">{s(selected.tag)}</span>
                    <h2 className="ul-dondir-detail-title">{s(selected.title)}</h2>

                    <div className="ul-dondir-meter">
                      <ProgressBar value={selected.progress} containerClassName="ul-dondir-meter-bar" />
                      <dl className="ul-dondir-meter-figures">
                        {/* The goal is the Foundation's stated figure. Raised is the API's
                            sum of completed payments — nought until PayPal is wired, and
                            nought is worded rather than printed: this page cannot see a gift
                            the Foundation received some other way. */}
                        <div>
                          <dt>{t('donate.raised')}</dt>
                          <dd>
                            {selected.raised > 0
                              ? formatMoney(selected.raised, selected.currency, intlLocale)
                              : t('donate.raised.unconfirmed')}
                          </dd>
                        </div>
                        <div>
                          <dt>{t('donate.goal')}</dt>
                          <dd>{formatMoney(selected.goal, selected.currency, intlLocale)}</dd>
                        </div>
                      </dl>
                    </div>

                    {/*
                      Who has given. Zero is the true number — there is no payment provider
                      — and at zero it reads as an invitation rather than as a digit: "0
                      people" is true and useless, "Be the first to give" is true and does
                      something. Neither invents a donor.
                    */}
                    <p className="ul-dondir-detail-people">
                      <i className="flaticon-team" aria-hidden="true" />
                      {selected.donations > 0
                        ? t('donate.people', { count: selected.donations })
                        : t('donate.beFirst')}
                    </p>
                  </div>
                </div>

                <div className="ul-dondir-detail-body">
                  {/* The opening paragraph only. The rest is behind "read the full
                      appeal", which is what keeps the collapsed panel on one screen. */}
                  {lead && <p className="ul-dondir-detail-txt">{s(lead)}</p>}

                  {/*
                    The rest of the appeal, in the card.

                    It was a link to `/donations/<slug>` and now it is a disclosure: a
                    directory whose "read more" navigates away is a directory you have to
                    leave to use. What opens here is the remaining paragraphs **and nothing
                    else** — the giving form was in it too until 2026-09-25, so most donors
                    never saw the personal details. It is under the appeal now, always.

                    No button for an appeal of one paragraph: a disclosure that opens nothing
                    is a control that lies about the page.
                  */}
                  {rest.length > 0 && (
                    <button
                      type="button"
                      className="ul-dondir-detail-more"
                      aria-expanded={full}
                      aria-controls={fullId}
                      onClick={() => setFull((open) => !open)}
                    >
                      {full ? t('donate.readLess') : t('donate.readFull')}
                      <i className={full ? 'flaticon-up-right-arrow' : 'flaticon-down'} aria-hidden="true" />
                    </button>
                  )}

                  {rest.length > 0 && (
                    <div className="ul-dondir-full" id={fullId} hidden={!full}>
                      {rest.map((paragraph) => (
                        <p className="ul-dondir-detail-txt" key={paragraph.en}>
                          {s(paragraph)}
                        </p>
                      ))}
                    </div>
                  )}

                  {/*
                    The give action, LAST — under the appeal whether it is open or shut.

                    It sat above the disclosure until now, so opening the full appeal pushed
                    the detail below the button that acts on it: you read the case for giving
                    after passing the control for it. Whatever else the card is showing, the
                    thing you do about it is at the bottom.

                    There is no payment-method choice here. PayPal will handle the payment
                    and its own window is where a method is chosen, so asking twice would be
                    asking once too many — and asking BEFORE the provider exists would be
                    inventing a set of methods the Foundation has not agreed to.

                    The form is `GiveForm`: the details first, then the amounts, then the
                    button — and it posts. A closed appeal takes no gifts, so its panel shows
                    no form at all rather than one the API would refuse.
                  */}
                  <div className="ul-dondir-give">
                    {selected.status === 'active' ? (
                      <GiveForm appeal={selected.slug} currency={selected.currency} />
                    ) : (
                      <p className="ul-dondir-give-note">{t('donate.closed')}</p>
                    )}

                    {/*
                      What the Foundation is, beside the place somebody decides to give.

                      A donor is entitled to know who is receiving the money and on what
                      terms, and the two facts that follow from 501(c)(3) are the ones that
                      actually change their decision: the gift is deductible if they pay US
                      tax, and nothing given can end up benefiting a private individual.

                      **The EIN is stated as missing rather than filled in**, exactly as the
                      street address and the telephone number are across this platform. It
                      is the number a donor quotes to claim, so a wrong one is not a cosmetic
                      error — it is a person's tax return.
                    */}
                    <aside className="ul-dondir-status">
                      <h4 className="ul-dondir-status-title">{t('donate.status.title')}</h4>
                      <p>{t('donate.status.body')}</p>
                      <p className="ul-dondir-status-gap">{t('donate.status.ein')}</p>
                    </aside>
                  </div>
                </div>
              </article>
            )}
          </div>
        </div>
      </section>


      {/*
        JOIN & GIVE, under the directory.

        The appeals are what somebody opened this page for, so they come first; these are
        the two broader asks — join, give — for a reader who has read them and wants
        something wider than one cause.
      */}
      <SplitPanels
        left={{
          title: s(PARTNERS_AND_GIVE.left.title),
          descr: s(PARTNERS_AND_GIVE.left.descr),
          list: PARTNERS_AND_GIVE.left.list.map(s),
          action: (
            // `href`, not `to` — Partners is its own app on its own domain, and
            // `SmartLink` under `Btn` is what knows the difference.
            <Btn href={PARTNERS_AND_GIVE.left.ctaHref} icon="fast-forward-double-right-arrows-symbol">
              {s(PARTNERS_AND_GIVE.left.ctaLabel)}
            </Btn>
          ),
        }}
        right={{
          title: s(PARTNERS_AND_GIVE.right.title),
          descr: s(PARTNERS_AND_GIVE.right.descr),
          action: (
            <DonateAmountPicker amounts={DONATE_AMOUNTS} as="form" className="ul-donate-form-2">
              <div>
                <Btn type="submit" icon="fast-forward-double-right-arrows-symbol">
                  {s(PARTNERS_AND_GIVE.right.ctaLabel)}
                </Btn>
              </div>
            </DonateAmountPicker>
          ),
        }}
      />
    </>
  )
}
